import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Sparkles, ArrowRight } from 'lucide-react';
import Button from '@/components/ui/Button';

interface PricingPack {
  name: string;
  emoji: string;
  subtitle: string;
  objective: string;
  price: string;
  duration: string;
  highlight: string;
  features: string[];
  highlighted?: boolean;
}

const packs: PricingPack[] = [
  {
    name: "Prototype Start",
    emoji: "🧩",
    subtitle: "Donnez forme à votre idée",
    objective: "Obtenir une première version présentable et crédible pour séduire vos premiers partenaires, incubateurs ou testeurs.",
    price: "3 900 €",
    duration: "2 à 3 semaines",
    highlight: "un prototype pro, une landing claire et un deck prêt à partager — tout pour exister dès maintenant.",
    features: [
      "Session de cadrage offerte (2h avec un Product Builder Orcsys)",
      "Arborescence claire + wireframes interactifs (Figma)",
      "Design UI léger (système de composants Orcsys)",
      "Prototype interactif (Figma ou no-code léger)",
      "Check-list \"Prêt pour le test\"",
      "One-pager de présentation du concept",
      "Mini scoring de faisabilité technique (vision + estimation V1)",
      "Landing page de présentation (1 page — storytelling + CTA inscription ou contact)",
      "Mini deck de présentation (5 slides : problème, solution, design, prototype, next steps)"
    ]
  },
  {
    name: "Prototype Test",
    emoji: "🧠",
    subtitle: "Validez par la preuve",
    objective: "Tester l'usage, récolter du feedback et obtenir des preuves mesurables avant d'aller plus loin.",
    price: "5 500 €",
    duration: "3 à 4 semaines",
    highlight: "vous obtenez des données concrètes, des retours utilisateurs et une vision claire pour la suite.",
    features: [
      "Tout le contenu du Prototype Start",
      "Prototype fonctionnel (no-code ou low-code)",
      "5 à 10 tests utilisateurs réels",
      "Dashboard de suivi (clics, temps, friction)",
      "Rapport d'analyse automatique (via dashboard Orcsys)",
      "Score de validation par hypothèse (Go / À itérer / À revoir)",
      "Replay des sessions de test (si applicable)",
      "Template de roadmap post-prototype",
      "Audit UX express sur les résultats de test",
      "Session de débrief (1h)"
    ],
    highlighted: true
  },
  {
    name: "Prototype Proof",
    emoji: "🚀",
    subtitle: "Impressionnez et levez",
    objective: "Créer un prototype présentable, mesurable et irrésistible pour investisseurs, partenaires et comités d'innovation.",
    price: "9 900 €",
    duration: "5 à 6 semaines",
    highlight: "un prototype qui impressionne, un site immersif et un dossier prêt à convaincre dès la première démo.",
    features: [
      "Tout le contenu du Prototype Test",
      "Dashboard de performance enrichi (feedbacks + synthèse \"Go / No Go\")",
      "Site de démo immersif autour du prototype (inscriptions, storytelling, liste d'attente)",
      "Vidéo de démo professionnelle (motion courte ou screencast scénarisé)",
      "Mini \"Investor Deck\" (6–8 slides personnalisées avec KPIs)",
      "Mode \"Démo Pitch\" (parcours fluide pour présentation live ou salon)",
      "Intégration API / IA légère (chatbot, recommandation, calcul dynamique, etc.)",
      "Investor Pack automatisé (PDF complet : deck, métriques, roadmap, lien démo)"
    ]
  }
];

const PricingSection = () => {
  return (
    <section id="packs" className="py-20 lg:py-24 bg-slate-50">
      <div className="container px-4 mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Nos 3 formules — À chaque étape, son prototype
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {packs.map((pack, index) => (
            <motion.div
              key={index}
              className={`relative rounded-2xl overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:scale-[1.02] hover:shadow-xl ${
                pack.highlighted 
                  ? 'bg-gradient-to-br from-orcsys-500 to-orcsys-600 text-white' 
                  : 'bg-white hover:border-orcsys-200'
              }`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              {pack.highlighted && (
                <div className="absolute top-0 right-0 bg-white text-orcsys-500 px-4 py-1 rounded-bl-lg text-sm font-medium">
                  Recommandé
                </div>
              )}

              <div className="p-8">
                <h3 className={`text-2xl font-bold mb-1 ${
                  pack.highlighted ? 'text-white' : 'text-slate-900'
                }`}>
                  {pack.name}
                </h3>
                <p className={`text-lg font-medium mb-4 ${
                  pack.highlighted ? 'text-white/90' : 'text-slate-700'
                }`}>
                  {pack.subtitle}
                </p>
                
                <div className="mb-4">
                  <p className={`text-sm font-medium mb-2 ${
                    pack.highlighted ? 'text-white/80' : 'text-slate-600'
                  }`}>
                    Objectif :
                  </p>
                  <p className={`text-sm mb-4 ${
                    pack.highlighted ? 'text-white/90' : 'text-slate-700'
                  }`}>
                    {pack.objective}
                  </p>
                </div>

                <div className="flex items-baseline mb-4">
                  <span className={`text-4xl font-bold ${
                    pack.highlighted ? 'text-white' : 'text-slate-900'
                  }`}>{pack.price}</span>
                  <span className={`ml-2 ${
                    pack.highlighted ? 'text-white/80' : 'text-slate-500'
                  }`}>HT</span>
                </div>
                <p className={`text-sm mb-6 ${
                  pack.highlighted ? 'text-white/90' : 'text-slate-600'
                }`}>
                  Durée : {pack.duration}
                </p>

                <div className="mb-6">
                  <p className={`text-sm font-medium mb-2 ${
                    pack.highlighted ? 'text-white' : 'text-slate-900'
                  }`}>
                     Vous obtenez :
                  </p>
                  <ul className="space-y-2">
                    {pack.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle className={`w-4 h-4 mr-2 flex-shrink-0 mt-1 ${
                          pack.highlighted ? 'text-white' : 'text-orcsys-500'
                        }`} />
                        <span className={`text-xs ${
                          pack.highlighted ? 'text-white/90' : 'text-slate-600'
                        }`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={`mb-6 p-3 rounded-lg ${
                  pack.highlighted ? 'bg-white/20' : 'bg-orcsys-50'
                }`}>
                  <p className={`text-xs font-medium ${
                    pack.highlighted ? 'text-white' : 'text-slate-900'
                  }`}>
                    🟢 Le point fort : {pack.highlight}
                  </p>
                </div>

                <Button.Link
                  href="#contact"
                  className={`w-full justify-center group ${
                    pack.highlighted 
                      ? 'bg-white text-orcsys-600 hover:bg-white/90'
                      : 'bg-orcsys-500 text-white hover:bg-orcsys-600'
                  }`}
                  onClick={() => {
                    const contactSection = document.getElementById('contact');
                    if (contactSection) {
                      contactSection.scrollIntoView({ behavior: 'smooth' });
                      const packSelect = document.getElementById('pack') as HTMLSelectElement;
                      if (packSelect) {
                        packSelect.value = pack.name;
                        const event = new Event('change', { bubbles: true });
                        packSelect.dispatchEvent(event);
                      }
                    }
                  }}
                >
                  Choisir ce pack
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button.Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;

